import { useBranchStore } from '@/store/branchStore';
import { GivePillIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';

interface HeroProps {
  isLoading?: boolean;
}

const PHARMACY_CATEGORIES = [
  { name: 'Prescription Drugs', color: 'var(--primary)', textColor: 'white' },
  {
    name: 'Over the Counter',
    color: 'var(--famasi-xanthous-yellow-300)',
    textColor: 'var(--dispensary-gray-900)',
  },
  {
    name: 'Health & Wellness',
    color: 'var(--famasi-green-100)',
    textColor: 'var(--dispensary-gray-900)',
  },
  {
    name: 'Medical Supplies',
    color: 'var(--famasi-crayola-blue-100)',
    textColor: 'var(--dispensary-gray-900)',
  },
];

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1671108503276-1d3d5ab23a3a?q=80&w=2232&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';

export function Hero({ isLoading }: HeroProps) {
  const getHeadline = useBranchStore(s => s.getHeadline);
  const getDescription = useBranchStore(s => s.getDescription);
  const channelLoading = useBranchStore(s => s.channelLoading);
  const storefrontChannel = useBranchStore(s => s.storefrontChannel);

  const channelHeadline = getHeadline();
  const channelDescription = getDescription();
  const premiseImages = storefrontChannel?.premiseImages || [];

  const heroTitle = channelHeadline || 'Your Trusted Online Pharmacy';

  const heroSubtitle =
    channelDescription ||
    'Access quality medications and healthcare products with professional guidance, delivered right to your doorstep.';

  const heroImage = premiseImages[0] || FALLBACK_IMAGE;

  const isDataLoading = isLoading || channelLoading;

  return (
    <div className="relative hero-bg py-16 md:py-20 transition-colors duration-200 ease-out-quad bg-primary-bg">
      <div className="container mx-auto px-6 md:px-16 lg:px-24">
        <div className="max-w-2xl flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            {isDataLoading ? (
              <>
                <Skeleton className="h-8 w-full max-w-xl mb-1. 5" />
                <Skeleton className="h-4 w-full max-w-lg" />
                <Skeleton className="h-4 w-full max-w-md" />
              </>
            ) : (
              <>
                <h1 className="text-primary text-3xl md:text-4xl font-semibold">
                  {heroTitle}
                </h1>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {heroSubtitle}
                </p>
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PHARMACY_CATEGORIES.map(category => (
              <Badge
                key={category.name}
                variant="secondary"
                className="rounded-full text-[10px] md:text-xs font-medium flex items-center gap-1 px-2.5 py-1 shadow-sm hover:shadow-md transition-all duration-200 ease-out-cubic"
                style={{
                  backgroundColor: category.color,
                  color: category.textColor,
                  borderColor: 'transparent',
                }}
              >
                <HugeiconsIcon icon={GivePillIcon} size={10} />
                {category.name}
              </Badge>
            ))}
          </div>
        </div>
      </div>
      <div
        className="absolute inset-y-0 right-0 w-1/3 bg-cover bg-center hidden lg:block opacity-100 dark:opacity-70"
        style={{ backgroundImage: `url('${heroImage}')` }}
      />
    </div>
  );
}
